import { DraggableItemIcon, FormEditIcon } from 'assets/icons';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import { If } from 'components';
import {
  selectCurrentTeamAccountingPermissions,
  selectCurrentTeamFeePermissions,
  showNotifier
} from 'containers/app/appSlice';
import AddInvoiceDiscountWrapper from 'containers/invoices/create-invoice/components/AddInvoiceDiscountWrapper';
import AddItemModal, { IAddItemModalConfig } from 'containers/invoices/create-invoice/components/AddItemModal';
import AddLineItemSectionWrapper from 'containers/invoices/create-invoice/components/AddLineItemSectionWrapper';
import AddServiceChargeWrapper from 'containers/invoices/create-invoice/components/AddServiceChargeWrapper';
import AddServiceFeeWrapper from 'containers/invoices/create-invoice/components/AddServiceFeeWrapper';
import { InvoiceValidationErrorType } from 'containers/invoices/create-invoice/constants';
import {
  EInvoiceCatalogElementType,
  EInvoiceModalType,
  EItemModalType,
  ISaveLineItem,
  InvoiceDraftModalData
} from 'containers/invoices/create-invoice/types';
import { InvoiceLineItem } from 'containers/invoices/InvoicesSlice';
import { DiscountFormData, LineItemAdditionalTax } from 'containers/invoices/types';
import { getAggregatedTaxInCurrencyWithSymbol } from 'containers/invoices/utils';
import { CreateJobContextType } from 'containers/jobs/types';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, createContext, useContext, useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import { formattedLineItemQty, getAmountWithCurrency } from 'utils/amountUtils';
import { CreateEditJobNavEnum } from 'containers/jobs/constant';
import {
  addJobLineItem,
  incrementJobLineItemQty,
  modifyJobLineItem,
  removeJobDiscountFormData,
  removeJobLineItem,
  removeJobServiceCharge,
  resetJobDraftData,
  selectAppliedJobLineItemLevelTaxes,
  selectCurrentSurchargeInJobs,
  selectGlobalLineItemTaxesJobsSlice,
  selectGlobalServiceChargeTaxesInJobSlice,
  selectIsBackFromCreateTaxFromJobsSlice,
  selectIsServiceFeeEnabledFromJob,
  selectJobDiscountFormData,
  selectJobDiscountSettings,
  selectJobFinalAmount,
  selectJobInvoiceStatus,
  selectJobLineItemDraftModalData,
  selectJobLineItems,
  selectJobLineItemsTotal,
  selectJobSelectedServiceCharge,
  selectJobSubtotal,
  selectJobValidationErrors,
  selectTotalOriginalUnitCost,
  updateIsBackFromCreateTaxInJobsSlice,
  updateIsServiceFeeEnabledInJobs,
  updateJobDiscountFormData,
  updateJobDraftData,
  updateLineItemsOrderInJobsSlice,
  updateSelectedJobServiceCharge
} from '../createJobSlice';
import { LINE_ITEM_SOURCE } from 'constants/common';
import { IInvoiceServiceCharge, InvoiceStatus } from 'containers/invoices/invoices.model';
import { NotifierTypes } from 'constants/notifierConstants';
import { twMerge } from 'tailwind-merge';

interface JobLineItemSectionProps {
  className?: string;
  isLoadingTaxesAndFees: boolean;
  context: React.Context<CreateJobContextType>;
}

const addItemModalConfigInitState: IAddItemModalConfig = {
  open: false,
  data: {
    id: 0,
    name: '',
    unitPrice: '',
    sku: '',
    description: '',
    selectedTaxes: [],
    quantity: '1',
    unitCost: '',
    markupPercentage: ''
  },
  editMode: false
};

export interface ILineItemsSectionContext {
  showAddFromCatalogModal: boolean;
  setShowAddFromCatalogModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const JobLineItemsSectionContext = createContext<ILineItemsSectionContext>({
  showAddFromCatalogModal: false,
  setShowAddFromCatalogModal: undefined
});

const JobLineItemSection: FC<JobLineItemSectionProps> = ({ isLoadingTaxesAndFees, context }) => {
  // Update styles for billing tab

  const isBackFromCreateTaxRoute = useAppSelector(selectIsBackFromCreateTaxFromJobsSlice);
  const selectedJobDraftData = useAppSelector(selectJobLineItemDraftModalData);
  const currentInvoiceValidationErrors = useAppSelector(selectJobValidationErrors);
  const currentSurchargeState = useAppSelector(selectCurrentSurchargeInJobs);
  const discountSettingsState = useAppSelector(selectJobDiscountSettings);
  const selectedServiceCharge = useAppSelector(selectJobSelectedServiceCharge);
  const lineItemsTotalState = useAppSelector(selectJobLineItemsTotal);
  const jobLineItemsState = useAppSelector(selectJobLineItems);
  const discountFormDataState = useAppSelector(selectJobDiscountFormData);
  const jobFinalAmountState = useAppSelector(selectJobFinalAmount);
  const jobSubtotalState = useAppSelector(selectJobSubtotal);
  const jobFeePermissions = useAppSelector(selectCurrentTeamFeePermissions);
  const selectedAppliedLineItemLevelTaxes = useAppSelector(selectAppliedJobLineItemLevelTaxes);
  const totalOriginalUnitCostState = useAppSelector(selectTotalOriginalUnitCost);
  const { viewItemCost } = useAppSelector(selectCurrentTeamAccountingPermissions);

  const [showAddFromCatalogModal, setShowAddFromCatalogModal] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { selectedJobTab, setSelectedJobTab } = useContext<CreateJobContextType>(context);
  const isTabSelected = selectedJobTab === CreateEditJobNavEnum.LINE_ITEMS;
  const [addItemModalConfig, setAddItemModalConfig] = useState<IAddItemModalConfig>(addItemModalConfigInitState);
  const [items, setItems] = useState([]);

  const invoiceStatus = useAppSelector(selectJobInvoiceStatus);
  const isPaidInvoice =
    invoiceStatus === InvoiceStatus.PAID ||
    invoiceStatus === InvoiceStatus.PAID_QR ||
    invoiceStatus === InvoiceStatus.REFUNDED ||
    invoiceStatus === InvoiceStatus.REFUND_INITIATED;

  useEffect(() => {
    setItems(jobLineItemsState); // while navigating & returning, item order resets
  }, [jobLineItemsState]);

  const showDraftEditItemModal = () => {
    const { modalConfig } = selectedJobDraftData ?? {};
    const itemModalConfig: IAddItemModalConfig = {
      open: true,
      data: modalConfig?.data ?? addItemModalConfigInitState,
      editMode: modalConfig?.editMode ?? false,
      draftFlow: modalConfig?.draftFlow
    };
    if (selectedJobDraftData.invoiceModalType === EInvoiceModalType.EDIT_ITEM_MODAL) {
      setAddItemModalConfig({
        data: itemModalConfig.data,
        open: true,
        editMode: true,
        draftFlow: { isDraftFlow: true, saveToCatalog: false }
      });

      // Add, one-time modals are rendered in addlineitemsection wrapper
      if (isBackFromCreateTaxRoute) {
        handleOnBackFromCreateTax();
      }
    }
  };

  const handleOnBackFromCreateTax = () => {
    dispatch(resetJobDraftData());
    dispatch(updateIsBackFromCreateTaxInJobsSlice(false));
  };

  useEffect(() => {
    if (isBackFromCreateTaxRoute) {
      if (
        selectedJobDraftData &&
        selectedJobDraftData?.invoiceCatalogElementType === EInvoiceCatalogElementType.ITEM &&
        selectedJobDraftData?.invoiceModalType === EInvoiceModalType.EDIT_ITEM_MODAL
      ) {
        showDraftEditItemModal();
      }
    }
  }, []);

  const getTotalUnitCost = () => {
    return getAmountWithCurrency(totalOriginalUnitCostState);
  };

  const resetItemModalConfig = () => {
    setAddItemModalConfig(addItemModalConfigInitState);
  };

  const onRemoveLineItem = (lineItem: InvoiceLineItem) => {
    dispatch(removeJobLineItem(lineItem));
  };

  const onEditLineItem = (lineItem: InvoiceLineItem) => {
    if (isPaidInvoice) {
      dispatch(
        showNotifier({
          type: NotifierTypes.WARNING,
          message: {
            primaryMessage: 'Editing line items is disabled for jobs with paid invoice',
            isMessageHtml: false
          }
        })
      );
      return;
    }
    const itemData = {
      id: +lineItem.id,
      catalogueLineItemId: lineItem.catalogueLineItemId,
      name: lineItem.itemDescription,
      unitPrice: lineItem.unitCost,
      unitCost: lineItem.originalUnitCost,
      markupPercentage: lineItem.markupPercentage,
      sku: lineItem.sku,
      description: lineItem.additionalDetails,
      selectedTaxes: lineItem?.taxes ?? [],
      quantity: lineItem.quantity
    };
    setAddItemModalConfig({ open: true, data: itemData, editMode: true });
  };

  const onSaveLineItemsClick = (lineItemData: ISaveLineItem) => {
    dispatch(addJobLineItem(lineItemData.invoiceLineItem));
    resetItemModalConfig();
  };

  const onModifyLineItemClick = (lineItem: InvoiceLineItem) => {
    dispatch(modifyJobLineItem(lineItem));
    resetItemModalConfig();
  };

  const isAddDiscountDisabled =
    jobLineItemsState?.length === 0 || jobLineItemsState === null || !jobFeePermissions.viewDiscount || isPaidInvoice;

  const isAddServiceChargeDisabled = jobLineItemsState?.length === 0 || jobLineItemsState === null || isPaidInvoice;

  const getFormattedSubtotalCurrency = () => {
    if (!jobSubtotalState) {
      return getAmountWithCurrency(0);
    }
    const subTotal = jobSubtotalState;
    return getAmountWithCurrency(subTotal);
  };

  const getCurrencyFormattedFinalAmount = () => {
    return getAmountWithCurrency(jobFinalAmountState);
  };

  const getLineItemErrorMsg = () => {
    const lineItemError = currentInvoiceValidationErrors.find(error => {
      if (error.type === InvoiceValidationErrorType.MIN_LINE_ITEM) {
        return error;
      }
    });
    return lineItemError?.errorMsg;
  };

  const onCloseAddItemModal = (): void => {
    setAddItemModalConfig(addItemModalConfigInitState);
  };

  const getLineItemTaxesText = (lineItemTaxes: LineItemAdditionalTax[]) => {
    if (lineItemTaxes?.length === 1) return '+1 tax';
    return `+${lineItemTaxes?.length} taxes`;
  };

  const handleDragStart = (e, data, index) => {
    const draggedItemDiv = document.getElementById(`draggable-line-item-div-${index}`);
    if (draggedItemDiv) {
      draggedItemDiv.style.zIndex = '10';
    }
  };

  const handleDrag = (e, data, index) => {
    const itemDivHeight = getHeightOfDiv(index);
    let newIndex = index + Math.round(data.lastY / itemDivHeight);

    if (newIndex < 0) {
      newIndex = 0;
    } else if (newIndex >= items.length) {
      newIndex = items.length - 1;
    }

    if (index !== newIndex) {
      const newItems = [...items];
      const movedItem = newItems.splice(index, 1)[0];
      newItems.splice(newIndex, 0, movedItem);

      setItems(newItems);
      dispatch(updateLineItemsOrderInJobsSlice(newItems));
    }

    // Dynamically adjust the z-index while dragging
    const draggedItemDiv = document.getElementById(`draggable-line-item-div-${index}`);
    if (draggedItemDiv) {
      const zIndex = items.length - newIndex;
      draggedItemDiv.style.zIndex = zIndex.toString();
    }
  };

  const getHeightOfDiv = (index: number) => {
    const div = document.querySelector(`#draggable-line-item-div-${index}`);
    if (div) {
      const elementRect = div.getBoundingClientRect();
      return elementRect.height;
    }
    return 0;
  };

  const handleIncrementLineItemQty = (itemToBeAdded: InvoiceLineItem) => {
    dispatch(incrementJobLineItemQty(itemToBeAdded));
  };

  const handleUpdateInvoiceModalData = (invoiceItemModalDraft: InvoiceDraftModalData) => {
    dispatch(updateJobDraftData(invoiceItemModalDraft));
  };

  const onRemoveDiscount = (): void => {
    dispatch(removeJobDiscountFormData());
  };

  const handleSaveDiscount = (discountFormData: DiscountFormData): void => {
    dispatch(updateJobDiscountFormData(discountFormData));
  };

  const handleIsServiceFeeEnabled = (isServiceFeeEnabled: boolean): void => {
    dispatch(updateIsServiceFeeEnabledInJobs(isServiceFeeEnabled));
  };

  const handleRemoveSelectedServiceCharge = () => {
    dispatch(removeJobServiceCharge());
  };

  const handleUpdateSelectedServiceCharge = (serviceCharge: IInvoiceServiceCharge): void => {
    dispatch(updateSelectedJobServiceCharge(serviceCharge));
  };

  const onFocus = () => {
    setSelectedJobTab(CreateEditJobNavEnum.LINE_ITEMS);
  };

  return (
    <JobLineItemsSectionContext.Provider value={{ showAddFromCatalogModal, setShowAddFromCatalogModal }}>
      <>
        <div onFocus={onFocus}>
          <div className={twMerge('mb-2 font-bold text-xl', isTabSelected ? 'text-primaryBtn' : 'text-heading')}>
            Line items
          </div>

          <div
            className={`flex flex-col rounded-md border px-8 py-6 ${
              isTabSelected ? 'border-primaryBtn' : 'border-borderGray'
            }`}>
            {/* lint item headers */}
            <If condition={jobLineItemsState?.length > 0}>
              <div className="mb-4 flex flex-row text-sbase font-semibold text-primaryText">
                <div className="w-1/2 min-w-[60px] whitespace-nowrap">Item</div>
                <div className="w-[15%] min-w-[60px] whitespace-nowrap">Unit price</div>
                <div className="w-[15%] min-w-[60px] whitespace-nowrap text-center">Quantity</div>
                <div className="w-[20%] min-w-[60px] whitespace-nowrap pr-5 text-right">Total</div>
              </div>
            </If>
            <div className="space-y-5">
              <div className="relative space-y-5">
                {items?.map((lineItem, index) => {
                  return (
                    /* LineItemCard */
                    <Draggable
                      key={index}
                      handle=".draggableContainer"
                      axis="y"
                      position={{ x: 0, y: 0 }}
                      onStart={(e, data) => handleDragStart(e, data, index)}
                      onStop={(e, data) => handleDrag(e, data, index)}
                      bounds={'parent'}>
                      <div
                        id={`draggable-line-item-div-${index}`}
                        key={lineItem?.itemDescription + index}
                        className={`draggableContainer relative flex min-h-[85px] w-full cursor-move flex-row
                           rounded border border-borderGray bg-white py-4`}>
                        <div className="flex w-1/2 min-w-[60px] flex-col gap-3 pl-5 pr-2">
                          <div
                            onClick={() => onEditLineItem(lineItem)}
                            className="line-clamp-8 cursor-pointer break-words 
                              text-17px font-semibold text-secondary">
                            {lineItem?.itemDescription}
                          </div>
                          <div className="whitespace-pre-line text-sbase font-normal text-primaryText line-clamp-3">
                            {lineItem?.additionalDetails}
                          </div>
                        </div>
                        <div className="w-[15%] min-w-[60px] truncate text-17px font-semibold text-primaryText">
                          {lineItem?.unitCost ? getAmountWithCurrency(lineItem?.unitCost, '$') : '$0'}
                        </div>
                        <div
                          className="flex w-[15%] min-w-[60px]
                     justify-center truncate text-17px font-semibold text-primaryText">
                          <div className="truncate">{formattedLineItemQty({ qtyString: lineItem?.quantity })}</div>
                        </div>
                        <div className="flex w-[20%] min-w-[60px] flex-col gap-3 pr-5">
                          <div
                            className="truncate text-right
                              text-17px font-semibold text-heading">
                            {getAmountWithCurrency(lineItem?.total)}
                          </div>
                          <If condition={lineItem?.taxes?.length > 0}>
                            <div className="text-right text-sbase font-normal text-primaryText">
                              {getLineItemTaxesText(lineItem?.taxes)}
                            </div>
                          </If>
                        </div>
                        {!isPaidInvoice && (
                          <>
                            <FormCloseReactIcon
                              id="create-job-remove-line-item-close-icon"
                              onClick={() => onRemoveLineItem(lineItem)}
                              className="absolute top-3 right-[-25px] shrink-0 cursor-pointer"
                            />
                            <FormEditIcon
                              id="create-job-line-item-edit-icon"
                              onClick={() => onEditLineItem(lineItem)}
                              className="absolute bottom-3 right-[-25px] mt-3 shrink-0 
                            cursor-pointer"
                            />
                          </>
                        )}
                        <DraggableItemIcon
                          className="absolute -left-5 top-1/2 shrink-0 
                          -translate-y-2/4 cursor-move 1439px:-left-4"
                        />
                      </div>
                    </Draggable>
                  );
                })}
              </div>

              {!isPaidInvoice && (
                <div>
                  <AddLineItemSectionWrapper
                    onSaveLineItemClick={onSaveLineItemsClick}
                    onModifyLineItemClick={onModifyLineItemClick}
                    context={JobLineItemsSectionContext}
                    from={LINE_ITEM_SOURCE.JOBS}
                    selectIsBackFromCreateTax={selectIsBackFromCreateTaxFromJobsSlice}
                    selectInvoiceDraftData={selectJobLineItemDraftModalData}
                    selectLineItems={selectJobLineItems}
                    onBackFromCreateTax={handleOnBackFromCreateTax}
                    incrementLineItemQty={handleIncrementLineItemQty}
                    selectGlobalLineItemTaxes={selectGlobalLineItemTaxesJobsSlice}
                    updateInvoiceModalData={handleUpdateInvoiceModalData}
                  />
                </div>
              )}

              <div className="-mt-2 mb-2 text-error">{getLineItemErrorMsg()}</div>

              {/* LineItemsTotal */}
              <div className="ml-auto w-1/2 min-w-[405px] rounded-md border border-x-borderGray">
                <If condition={discountSettingsState.enabled}>
                  <AddInvoiceDiscountWrapper
                    selectedDiscount={discountFormDataState}
                    isAddDiscountDisabled={isAddDiscountDisabled}
                    isEditInvoiceFlow={false}
                    lineItemsTotalCost={lineItemsTotalState}
                    handleRemoveDiscount={onRemoveDiscount}
                    onSaveDiscount={handleSaveDiscount}
                  />
                </If>

                <div
                  className="flex flex-row justify-between
                      border-b border-borderGray p-5 text-17px font-semibold text-heading">
                  <div>Subtotal</div>
                  <div id="create-job-line-items-subtotal-amount" className="w-0 grow break-words text-right">
                    {getFormattedSubtotalCurrency()}
                  </div>
                </div>
                <If condition={currentSurchargeState.enabled}>
                  <AddServiceFeeWrapper
                    serviceFee={currentSurchargeState}
                    isLoadingTaxesAndFees={isLoadingTaxesAndFees}
                    isEdit={false}
                    selectIsServiceFeeEnabled={selectIsServiceFeeEnabledFromJob}
                    onSaveIsServiceFeeEnabled={handleIsServiceFeeEnabled}
                    isEditDisabled={isPaidInvoice}
                    source={LINE_ITEM_SOURCE.JOBS}
                  />
                </If>
                <AddServiceChargeWrapper
                  isAddServiceChargeDisabled={isAddServiceChargeDisabled}
                  selectedServiceCharge={selectedServiceCharge}
                  selectIsBackFromCreateTax={selectIsBackFromCreateTaxFromJobsSlice}
                  selectInvoiceDraftData={selectJobLineItemDraftModalData}
                  onRemoveSelectedServiceCharge={handleRemoveSelectedServiceCharge}
                  onBackFromCreateTax={handleOnBackFromCreateTax}
                  updateSelectedServiceCharge={handleUpdateSelectedServiceCharge}
                  selectGlobalLineItemTaxes={selectGlobalServiceChargeTaxesInJobSlice}
                  updateInvoiceModalData={handleUpdateInvoiceModalData}
                />

                <If condition={selectedAppliedLineItemLevelTaxes?.length > 0}>
                  <div className="flex flex-col border-b border-borderGray p-5">
                    <div className="flex flex-col gap-[13px]">
                      {selectedAppliedLineItemLevelTaxes.map(aggregatedItemTax => (
                        <div className="flex flex-row items-start" key={aggregatedItemTax.id}>
                          <div className="w-1/2 break-words text-17px text-heading ">{aggregatedItemTax.name}</div>
                          {/* <div className="basis-1/4 text-right text-17px text-accentText">
                            {getFormattedAdditionalTaxPercent(aggregatedItemTax.percentage)}%
                          </div> */}
                          <div className="w-1/2 break-words text-right text-17px font-normal text-heading">
                            {getAggregatedTaxInCurrencyWithSymbol(
                              jobLineItemsState,
                              aggregatedItemTax,
                              selectedServiceCharge
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </If>
                <div className="flex flex-col gap-2 bg-borderGray p-5 text-xl font-bold text-heading">
                  <div className="flex flex-row items-center justify-between">
                    <div>Total amount</div>
                    <div id="create-job-line-items-total-amount" className="grow break-words text-right">
                      {getCurrencyFormattedFinalAmount()}
                    </div>
                  </div>
                  <If condition={viewItemCost}>
                    <div className="flex flex-row items-center justify-between text-sbase text-primaryText">
                      <div>Total unit cost</div>
                      <div id="create-job-line-items-total-cost" className="grow break-words text-right">
                        {getTotalUnitCost()}
                      </div>
                    </div>
                  </If>
                </div>
              </div>
            </div>
          </div>
        </div>

        {addItemModalConfig.open && (
          <AddItemModal
            title="Edit Item"
            isEdit={true}
            onSaveLineItemClick={onSaveLineItemsClick}
            onModifyLineItemClick={onModifyLineItemClick}
            config={addItemModalConfig}
            onClose={onCloseAddItemModal}
            resetItemModalConfig={onCloseAddItemModal}
            isEditInvoiceFlow={false}
            modalType={EItemModalType.EDIT_ITEM_MODAL}
            selectGlobalLineItemTaxes={selectGlobalLineItemTaxesJobsSlice}
            updateInvoiceModalData={handleUpdateInvoiceModalData}
            onRemoveItem={onRemoveLineItem}
          />
        )}
      </>
    </JobLineItemsSectionContext.Provider>
  );
};

export default JobLineItemSection;
